/**
 * engine/scratch-audio.ts — the browser behind the scratch's seam.
 *
 * Task 18. The production answer to `ScratchAudio` (`engine/scratch.ts`): Web Audio, the
 * processor file, `navigator.audioSession` and `fetch`. Nothing here decides anything — every
 * decision is the controller's, where a test can see it; this file is what jsdom cannot run,
 * and it is checked in a real browser over the preview instead
 * (`.omc/wiki/raw/zadacha-18-proverka.md`).
 *
 * ## Five details
 *
 * - **The processor is a file of our own site.** `?worker&url` makes the build bundle
 *   `scratch-worklet.ts` (with the arithmetic it imports) into its own hashed file under
 *   `/assets/` and hands back its address; the page's policy (`script-src 'self'`) lets that in
 *   and would refuse a `blob:`.
 * - **The decode rate is an offline context's.** `decodeAudioData` resamples to the rate of the
 *   context it is called on, so decoding on an `OfflineAudioContext` made at 24 000 gives the
 *   track at 24 000 without a second pass. Whether the phone takes 24 000 is asked by making one.
 * - **The voice has no inputs and two outputs.** It is a source, not an effect, and the
 *   speaker's two channels get the same mono voice.
 * - **The download is the element's URL, plain.** Same URL, same server cache; no `Range`, so
 *   the answer is the whole file; no cookie, the URL carries its own pass.
 * - **A parameter glides, it does not step** (task 45; since task 56 any parameter). A write with
 *   a glide is `setTargetAtTime` on the audio thread, so a write is never a click, and a write that
 *   comes late — a timer slowed in the background — is a slower slope, not a jump. How long the
 *   glide is, is the caller's (`engine/mixer.ts`). How it sounds on a phone in the background is
 *   **not checked**.
 * - **Nodes by id** (task 56): the seam hands out `{ id, kind }` and keeps the real nodes here, so
 *   nothing outside this file ever holds a Web Audio object.
 */

import type { MediaElementLike } from './element';
import type {
  DecodedAudio,
  ScratchAudio,
  ScratchContext,
  SoundInput,
  SoundNode,
  SoundNodeKind,
  VoicePort,
} from './scratch';
import { SCRATCH_PROCESSOR } from './scratch-protocol';
import type { FromVoice, ToVoice } from './scratch-protocol';
import workletUrl from './scratch-worklet?worker&url';

type ContextClass = new (options?: AudioContextOptions) => AudioContext;
type OfflineClass = new (channels: number, length: number, rate: number) => OfflineAudioContext;

interface AudioScope {
  readonly AudioContext?: ContextClass;
  readonly webkitAudioContext?: ContextClass;
  readonly OfflineAudioContext?: OfflineClass;
  readonly AudioWorkletNode?: unknown;
}

/** `navigator.audioSession` (Safari 16.4+): the one field written here. */
interface AudioSessionLike {
  type: string;
}

function wrap(context: AudioContext): ScratchContext {
  const nodes: AudioNode[] = [];
  const buffers = new WeakMap<AnalyserNode, Float32Array<ArrayBuffer>>();
  return {
    sampleRate: context.sampleRate,
    state: () => context.state,
    resume: () => context.resume(),
    suspend: () => context.suspend(),
    async voice(): Promise<VoicePort> {
      await context.audioWorklet.addModule(workletUrl);
      const node = new AudioWorkletNode(context, SCRATCH_PROCESSOR, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      // Task 57: connected by the scratch — into its deck's chain, or the speaker.
      const handle = keep('voice', node);
      return {
        node: handle,
        post(message: ToVoice, transfer?: Transferable[]): void {
          node.port.postMessage(message, transfer ?? []);
        },
        listen(hear: (message: FromVoice) => void): void {
          node.port.onmessage = (event: MessageEvent<FromVoice>) => {
            hear(event.data);
          };
        },
      };
    },
    source(el: MediaElementLike): SoundNode {
      if (!(el instanceof HTMLMediaElement)) throw new TypeError('not a media element');
      return keep('source', context.createMediaElementSource(el));
    },
    node(kind: SoundNodeKind): SoundNode {
      return keep(kind, make(context, kind));
    },
    connect(from: SoundNode, to: SoundInput): void {
      const source = real(from);
      if (to === 'speaker') {
        source.connect(context.destination);
        return;
      }
      if ('param' in to) {
        const param = paramOf(real(to.node), to.param);
        if (param !== undefined) source.connect(param);
        return;
      }
      source.connect(real(to));
    },
    set(node: SoundNode, name: string, value: number | string, glideS = 0): void {
      const target = real(node);
      if (typeof value === 'string') {
        // The one string a chain sets: a filter's type — a tone band, FILTER, the phaser's allpass.
        if (target instanceof BiquadFilterNode && name === 'type') {
          target.type = value as BiquadFilterType;
        }
        return;
      }
      const param = paramOf(target, name);
      if (param === undefined) return;
      const at = context.currentTime;
      param.cancelScheduledValues(at);
      if (glideS > 0) param.setTargetAtTime(value, at, glideS);
      else param.setValueAtTime(value, at);
    },
    response(node: SoundNode, channels: readonly Float32Array[]): void {
      const target = real(node);
      const first = channels[0];
      if (!(target instanceof ConvolverNode) || first === undefined) return;
      const buffer = context.createBuffer(channels.length, first.length, context.sampleRate);
      channels.forEach((channel, i) => {
        // A copy: `copyToChannel` takes an array over a plain `ArrayBuffer`, and the page's may be
        // over any buffer by its type.
        buffer.copyToChannel(new Float32Array(channel), i);
      });
      target.buffer = buffer;
    },
    disconnect(node: SoundNode): void {
      real(node).disconnect();
    },
    level(node: SoundNode): number {
      const target = real(node);
      if (!(target instanceof AnalyserNode)) return 0;
      // One buffer per analyser, read ten times a second.
      let samples = buffers.get(target);
      if (samples === undefined) {
        samples = new Float32Array(target.fftSize);
        buffers.set(target, samples);
      }
      target.getFloatTimeDomainData(samples);
      // The RMS, not the peak: a mastered track's peaks touch full scale all the time, and a
      // meter by them would stand in the red.
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      return Math.min(1, Math.sqrt(sum / samples.length));
    },
  };

  /** The real nodes behind the seam's ids. */
  function keep(kind: SoundNode['kind'], node: AudioNode): SoundNode {
    const handle = { id: nodes.length, kind };
    nodes.push(node);
    return handle;
  }

  function real(handle: SoundNode): AudioNode {
    const node = nodes[handle.id];
    if (node === undefined) throw new RangeError('no such node');
    return node;
  }
}

/** A node of `kind`; an oscillator is started, as a chain's swing must run from the first sample. */
function make(context: AudioContext, kind: SoundNodeKind): AudioNode {
  switch (kind) {
    case 'biquad':
      return context.createBiquadFilter();
    case 'delay':
      return context.createDelay(4);
    case 'gain':
      return context.createGain();
    case 'convolver':
      return context.createConvolver();
    case 'oscillator': {
      const oscillator = context.createOscillator();
      oscillator.start();
      return oscillator;
    }
    case 'analyser':
      return context.createAnalyser();
  }
}

/** The parameter `name` of `node`, where it has one. */
function paramOf(node: AudioNode, name: string): AudioParam | undefined {
  const value: unknown = (node as unknown as Record<string, unknown>)[name];
  return value instanceof AudioParam ? value : undefined;
}

export function webScratchAudio(): ScratchAudio {
  const scope = globalThis as AudioScope;
  const Context = scope.AudioContext ?? scope.webkitAudioContext;
  const Offline = scope.OfflineAudioContext;
  const supported =
    Context !== undefined &&
    Offline !== undefined &&
    typeof scope.AudioWorkletNode === 'function' &&
    'audioWorklet' in Context.prototype;

  return {
    supported,

    preferPlayback(): void {
      try {
        const session = (navigator as { audioSession?: AudioSessionLike }).audioSession;
        if (session !== undefined) session.type = 'playback';
      } catch {
        // A webview that refuses the write plays by its own rules; nothing else depends on it.
      }
    },

    createContext(): ScratchContext {
      if (Context === undefined) throw new Error('no AudioContext');
      return wrap(new Context({ latencyHint: 'interactive' }));
    },

    decodesAt(rate: number): boolean {
      if (Offline === undefined) return false;
      try {
        new Offline(1, 1, rate);
        return true;
      } catch {
        return false;
      }
    },

    async decode(data: ArrayBuffer, rate: number): Promise<DecodedAudio> {
      if (Offline === undefined) throw new Error('no OfflineAudioContext');
      return await new Offline(2, 1, rate).decodeAudioData(data);
    },

    async fetch(url: string, signal: AbortSignal): Promise<ArrayBuffer> {
      const response = await fetch(url, { signal, credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
      return await response.arrayBuffer();
    },
  };
}
